import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowCounterClockwise, CheckCircle, FlowerLotus, SpeakerSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useKidsSound } from "@/hooks/useKidsSound";
import { speakLumi, stopLumiAudio } from "@/lib/lumiAudio";
import { NoCookieYouTube } from "@/components/kids/NoCookieYouTube";
import { LUMI_POSES, LUMI_ALT } from "@/data/lumiPoses";
import { useLumi } from "@/components/LumiCharacter";

// Calm & Play: a follow-along finger movement video. No progress, points or badges — just watch and move.
// Plain youtube-nocookie.com embed (no IFrame API, no autoplay); volume lives inside the YouTube player.
const VIDEO_ID = "-nKUzWW1W2k";
const INTRO = "Hi friend! Let’s practice some finger moves! Watch the hands and follow along at your own pace. Press play when you're ready!";
const PANEL = "bg-white rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)]";
const CONFETTI_COLORS = ["#f2b544", "#f48fb1", "#2fb59f", "#6a3fc7", "#2fa6e0"];

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="fingers-confetti">
    {Array.from({ length: 28 }).map((_, i) => (
      <span
        key={i}
        className="lumi-confetti"
        style={{ left: `${(i * 37) % 100}%`, background: CONFETTI_COLORS[i % CONFETTI_COLORS.length], animationDelay: `${(i % 7) * 0.18}s`, animationDuration: `${2.4 + (i % 5) * 0.3}s` }}
      />
    ))}
  </div>
);

const DoneCheer = ({ onBack }) => {
  useEffect(() => { const t = setTimeout(onBack, 4500); return () => clearTimeout(t); }, [onBack]);
  return (
    <div className={`${PANEL} relative overflow-hidden p-8 sm:p-12 text-center`} data-testid="fingers-done-cheer">
      <Confetti />
      <img src={LUMI_POSES.celebrating} alt={LUMI_ALT.celebrating} draggable={false} className="w-32 h-40 sm:w-40 sm:h-52 object-contain mx-auto lumi-sprite-celebrate" />
      <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#3d2378] mt-4" data-testid="fingers-done-title">Great job!</h2>
      <p className="font-nunito text-base md:text-lg text-[#5b5572] mt-2">Your fingers followed along beautifully. Lumi is so proud of you!</p>
      <Button onClick={onBack} className="mt-6 bg-[#6a3fc7] hover:bg-[#5a33ad] text-white font-nunito font-extrabold rounded-full h-12 px-6" data-testid="fingers-done-back-btn">
        <ArrowLeft size={18} weight="bold" className="mr-2" /> Back to Kids Corner
      </Button>
    </div>
  );
};

export const FollowLumiFingers = ({ onBack }) => {
  const [soundOn] = useKidsSound();
  const lumi = useLumi();
  const [nonce, setNonce] = useState(0); // bump to reload the embed (Watch Again)
  const [done, setDone] = useState(false);

  useEffect(() => {
    lumi.hide(); // Lumi appears inline on this page, so the floating sticker stays hidden
    speakLumi(INTRO);
    return () => { stopLumiAudio(); lumi.show(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const watchAgain = useCallback(() => { stopLumiAudio(); setNonce((n) => n + 1); }, []);
  const allDone = useCallback(() => { stopLumiAudio(); setDone(true); }, []);

  if (done) return <div className="max-w-3xl mx-auto" data-testid="follow-lumi-fingers"><DoneCheer onBack={onBack} /></div>;

  return (
    <div className="max-w-5xl mx-auto" data-testid="follow-lumi-fingers">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-12 h-12 rounded-2xl bg-[#efe9fb] flex items-center justify-center shrink-0"><FlowerLotus size={28} weight="duotone" className="text-[#8b5cf6]" /></span>
        <div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#3d2378]" data-testid="fingers-title">Finger Moves with Lumi <span aria-hidden="true" className="text-[#f2b544] text-lg">✦</span></h2>
          <p className="font-nunito text-base text-[#5b5572]">A calm moment to watch, copy, and move together.</p>
        </div>
      </div>

      <div className={`${PANEL} overflow-hidden grid lg:grid-cols-5`} data-testid="fingers-card" data-nonce={nonce}>
        <div className="lg:col-span-3 bg-black">
          <div className="relative aspect-video w-full" data-testid="fingers-player">
            <NoCookieYouTube videoId={VIDEO_ID} title="Finger Moves with Lumi video" nonce={nonce} testId="fingers-iframe" />
          </div>
        </div>
        <div className="lg:col-span-2 p-6 sm:p-8 flex flex-col justify-center gap-4 bg-gradient-to-br from-[#f7f4fd] to-[#efe9fb]">
          <div className="flex items-start gap-3">
            <img src={LUMI_POSES.waving} alt={LUMI_ALT.waving} draggable={false} className="w-16 h-20 object-contain object-bottom shrink-0" data-testid="fingers-lumi-sprite" />
            <p className="font-nunito font-semibold text-base text-[#3d2378] bg-white/90 border border-[#e6ddfa] rounded-2xl px-4 py-3 flex-1" data-testid="fingers-lumi-message">{INTRO}</p>
          </div>
          <p className="font-nunito text-sm text-[#7a748f]">Use the video’s own volume button to adjust sound.</p>
          {!soundOn && (
            <p className="inline-flex items-center gap-1.5 font-nunito text-sm text-[#6a3fc7]" data-testid="fingers-sound-off-note">
              <SpeakerSlash size={16} weight="bold" /> Lumi’s voice is off — the video’s sound is set inside the player.
            </p>
          )}
          <div className="flex flex-wrap gap-3 mt-1">
            <Button onClick={watchAgain} variant="outline" className="font-nunito font-bold border border-[#d9ccf5] rounded-full text-[#4a2e8a] hover:bg-[#f3eefc] h-11 px-5" data-testid="fingers-watch-again-btn">
              <ArrowCounterClockwise size={18} weight="bold" className="mr-2" /> Watch Again
            </Button>
            <Button onClick={allDone} className="bg-[#2fb59f] hover:bg-[#279c89] text-white font-nunito font-extrabold rounded-full h-11 px-5" data-testid="fingers-all-done-btn">
              <CheckCircle size={18} weight="fill" className="mr-2" /> All Done
            </Button>
          </div>
        </div>
      </div>

      <button type="button" onClick={onBack} className="mt-8 inline-flex items-center gap-2 font-nunito font-bold text-base text-[#4a2e8a] hover:underline rounded-full px-3 py-2 focus-visible:ring-4 focus-visible:ring-[#b79cf0]" data-testid="fingers-back-btn">
        <ArrowLeft size={18} weight="bold" /> Back to Kids Corner
      </button>
    </div>
  );
};

export default FollowLumiFingers;
